import { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import shortId from 'shortid';
import { renderToString } from 'react-dom/server';

export default function useCellMeasurer({ items }) {
  const innerRef = useRef(null);

  const id = useMemo(shortId, []);

  const [hiddenSizingEl, setHiddenSizingEl] = useState(null);

  const [width, setWidth] = useState(0);

  const itemSize = useCallback(
    index => {
      if (!hiddenSizingEl) return 0;

      const item = items[index];
      hiddenSizingEl.innerHTML = renderToString(item);

      return hiddenSizingEl.clientHeight || 0;
    },
    [hiddenSizingEl, items.length],
  );

  useEffect(() => {
    /** @type {HTMLElement} */
    const innerEl = innerRef.current;
    if (!innerEl) return;
    if (hiddenSizingEl) return;

    const newHiddenSizingEl = document.createElement('div');
    const width = innerEl.clientWidth;
    newHiddenSizingEl.classList.add(`hidden-sizing-element-${id}`);
    newHiddenSizingEl.style.position = 'absolute';
    newHiddenSizingEl.style.top = '0';
    newHiddenSizingEl.style.width = `${width}px`;
    newHiddenSizingEl.style.pointerEvents = 'none';
    newHiddenSizingEl.style.visibility = 'hidden';

    setHiddenSizingEl(newHiddenSizingEl);

    document.body.appendChild(newHiddenSizingEl);
  }, [hiddenSizingEl, id]);

  useEffect(() => {
    return () => {
      const hiddenSizingElement = document.querySelector(
        `.hidden-sizing-element-${id}`,
      );

      document.body.removeChild(hiddenSizingElement);
    };
  }, [id]);

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;

    function handleResize() {
      const { width } = el.getBoundingClientRect();
      setWidth(width);
    }

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, []);

  const key = useMemo(shortId, [itemSize, hiddenSizingEl, width]);

  const style = hiddenSizingEl
    ? undefined
    : {
        visibility: 'hidden',
      };

  return {
    innerRef,
    itemSize,
    itemCount: items.length,
    key,
    style,
  };
}
